// src/pages/admins/finalPlanUtils.js
//
// Phase 90. Pure helpers for the final-plan (M1-M8 -> Module -> Screen)
// views of the Project Workspace. The payload comes from
// GET /projects/:id/final-plan (services/projectFinalPlanService.js);
// statuses and counts are computed there -- these helpers only label,
// filter and lay them out, never recompute progress.

// Keep in sync with PLAN_STATUS_LABELS in services/projectFinalPlanService.js.
export const PLAN_STATUS_META = {
  COMPLETED: { label: "Completed", tone: "success", icon: "✓" },
  COMPLETED_LATE: { label: "Completed Late", tone: "warning", icon: "✓" },
  READY_TO_COMPLETE: { label: "Ready to Complete", tone: "primary", icon: "⚑" },
  BLOCKED: { label: "Blocked", tone: "dark", icon: "⛔" },
  DELAYED: { label: "Delayed", tone: "danger", icon: "!" },
  AT_RISK: { label: "At Risk", tone: "warning", icon: "▲" },
  IN_PROGRESS: { label: "In Progress", tone: "info", icon: "●" },
  NOT_STARTED: { label: "Not Started", tone: "secondary", icon: "○" },
};

// Keep in sync with SCREEN_STATUS_LABELS (same service).
export const SCREEN_STATUS_META = {
  COMPLETED: { label: "Completed", tone: "success" },
  IN_PROGRESS: { label: "In Progress", tone: "info" },
  PENDING: { label: "Pending", tone: "secondary" },
  BLOCKED: { label: "Blocked", tone: "dark" },
  CANCELLED: { label: "Cancelled", tone: "light" },
  NOT_LINKED: { label: "No ticket", tone: "warning" },
};

export const VERIFICATION_META = {
  VERIFIED: { label: "Verified", tone: "success", title: "The workbook status is backed by a completed ticket." },
  PENDING: { label: "To verify", tone: "warning", title: "The workbook says Completed / To Verify, but no completed ticket confirms it yet." },
  MISMATCH: { label: "Mismatch", tone: "danger", title: "The workbook says Completed, but the linked ticket is not completed." },
};

export const DEPENDENCY_CONFLICT_LABELS = {
  STARTS_BEFORE_DEPENDENCY: "Starts before its prerequisite is planned to finish",
  ENDS_BEFORE_DEPENDENCY: "Ends before its prerequisite is planned to finish",
};

export const LINK_BASIS_LABELS = {
  EXCEL_TICKET_ID: "Excel Ticket ID",
  SAME_SCREEN_ID_ROW: "Same screen as a linked row",
  EXACT_TITLE: "Exact title match",
};

export function metaFor(map, key) {
  return map[key] || { label: key ? String(key).replace(/_/g, " ") : "—", tone: "secondary" };
}

export const EMPTY_PLAN_FILTERS = {
  search: "",
  milestoneId: "",
  status: "",
  workType: "",
  owner: "",
  verification: "",
  conflictsOnly: false,
};

export function planFiltersApplied(filters) {
  return Object.keys(EMPTY_PLAN_FILTERS).some((k) => filters[k] !== EMPTY_PLAN_FILTERS[k]);
}

const lc = (v) => String(v ?? "").toLowerCase();

function screenText(s) {
  return [
    s.screen_name, s.role, s.work_type, s.assignee_name, s.excel_owner, s.planned_owner?.name,
    s.status_label, s.excel_status, s.ticket ? `#${s.ticket.id}` : "", s.ticket?.title, s.ticket?.status_label,
    s.depends_on, s.source_task_title,
  ].map(lc).join(" | ");
}

function matchesScreenFilters(s, f) {
  if (f.status && s.status !== f.status) return false;
  if (f.workType && s.work_type !== f.workType) return false;
  if (f.owner && s.assignee_name !== f.owner) return false;
  if (f.verification && s.verification !== f.verification) return false;
  if (f.conflictsOnly && !s.dependency?.conflict) return false;
  return true;
}

// Narrows Milestone -> Module -> Screen by the toolbar filters. A search
// that matches a milestone (code, name, status) or a module name keeps all
// of its screens; otherwise only the screens whose own text matches.
// Returns the same shape with empty modules/milestones dropped.
export function filterFinalPlan(milestones, filters = EMPTY_PLAN_FILTERS) {
  const term = lc(filters.search).trim();
  const out = [];
  for (const m of milestones || []) {
    if (filters.milestoneId && String(m.id) !== String(filters.milestoneId)) continue;
    const milestoneHit = !term || [m.code, m.name, m.status_label].map(lc).some((t) => t.includes(term));
    const modules = [];
    for (const mod of m.modules || []) {
      const moduleHit = milestoneHit || lc(mod.name).includes(term) || lc(mod.status_label).includes(term);
      const screens = (mod.screens || []).filter(
        (s) => matchesScreenFilters(s, filters) && (moduleHit || screenText(s).includes(term))
      );
      if (screens.length) modules.push({ ...mod, screens });
    }
    if (modules.length) out.push({ ...m, modules });
  }
  return out;
}

export function countScreens(milestones) {
  return (milestones || []).reduce((n, m) => n + m.modules.reduce((k, mod) => k + mod.screens.length, 0), 0);
}

export function allScreens(milestones) {
  return (milestones || []).flatMap((m) => m.modules.flatMap((mod) => mod.screens));
}

// Distinct, sorted filter options taken from the payload itself (never a
// hardcoded owner or work-type list).
export function planFilterOptions(milestones) {
  const screens = allScreens(milestones);
  const uniq = (values) => [...new Set(values.filter(Boolean))].sort((a, b) => a.localeCompare(b));
  return {
    owners: uniq(screens.map((s) => s.assignee_name)),
    workTypes: uniq(screens.map((s) => s.work_type)),
  };
}

// Timeline geometry: position of a YYYY-MM-DD date between two others, 0-100.
export function datePercent(date, start, end) {
  if (!date || !start || !end) return null;
  const t = Date.parse(date);
  const a = Date.parse(start);
  const b = Date.parse(end);
  if (!(b > a)) return null;
  return Math.min(100, Math.max(0, ((t - a) / (b - a)) * 100));
}

// A bar from `from` to `to` (inclusive day), clamped to the chart range.
export function barGeometry(from, to, start, end) {
  const left = datePercent(from, start, end);
  const right = datePercent(to, start, end);
  if (left === null || right === null) return null;
  const dayPct = (24 * 3600 * 1000 * 100) / (Date.parse(end) - Date.parse(start));
  return { left, width: Math.max(right - left + dayPct, 0.6) };
}

// First-of-month ticks between two dates, for the timeline axis.
export function monthTicks(start, end) {
  if (!start || !end) return [];
  const ticks = [];
  const [y, m] = start.split("-").map(Number);
  let year = y;
  let month = m + 1;
  if (month > 12) { month = 1; year += 1; }
  for (;;) {
    const date = `${year}-${String(month).padStart(2, "0")}-01`;
    if (date > end) break;
    ticks.push(date);
    month += 1;
    if (month > 12) { month = 1; year += 1; }
  }
  return ticks;
}
